import { Fragment, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { Source, Status, Target } from '../types';
import { StatusPill } from './StatusPill';
import { KnowledgePanel } from './KnowledgePanel';
import { DevicePolicyPanel } from '../policies';
import { ActionLog } from './ActionLog';

interface Props {
  targets: Target[];
  /** Shapes the columns. Image Manager gets the folder tally; others do not. */
  source?: Source;
  /** Needed by the knowledge panel. Without it the panel simply does not load. */
  orgId?: string | null;
  emptyMessage?: string;
  /** Only owners and admins get the hide control. */
  canHide?: boolean;
  onHide?: (t: Target) => void;
}

/**
 * Grouped by client, sorted worst-first within each group.
 *
 * Note there is NO filter that hides rows based on a missing field. The old
 * dashboard dropped any Datto device with a blank client, which silently hid a
 * failed appliance whose last backup was two years old. Anything counted is
 * reachable.
 *
 * WHY THE "WHY" COLUMN EXPANDS. The reason used to be one clipped line with a
 * native title tooltip. Once the collectors started reporting every problem
 * rather than the worst one, that clipping destroyed the improvement: a machine
 * watching 27 folders had its explanation cut at an ellipsis with no way to
 * read the rest. A tooltip is not a fix - it cannot be selected, copied, or
 * read on a touch screen.
 *
 * WHY IMAGE MANAGER HAS ITS OWN COLUMNS. The old PHP site showed
 * `Machine / Site | Status | Folders | Healthy | Warning | Critical | Notes`
 * and that tally is the whole picture for a replication host. It is rendered
 * only for `sc`, because Datto and ShadowProtect have no managed folders and
 * four permanently empty columns are worse than none. Image Manager in turn
 * drops Last Backup, which it has no value for - the old site did not show it
 * either.
 */
/** Notes per machine id, and per client name. See noteCounts on the server. */
interface NoteCounts { target: Record<string, number>; client: Record<string, number> }

export function TargetTable({
  targets, source, orgId, emptyMessage = 'Nothing matches this filter.', canHide, onHide,
}: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  /*
   * WHICH MACHINES HAVE NOTES, known BEFORE anything is expanded.
   *
   * A note nobody can see is a note nobody reads: until this existed, the only
   * way to find out that a colleague had written down why a machine fails every
   * Saturday was to open the row and look. Somebody who did not already suspect
   * a note was there had no reason to.
   *
   * ONE CALL FOR THE WHOLE BOARD, not one per row - a per-row fetch would be
   * hundreds of round trips to render a badge. Failure is silent and simply
   * means no badges: a decoration must never be able to stop the board
   * rendering the thing it decorates.
   */
  const [noteCounts, setNoteCounts] = useState<NoteCounts>({ target: {}, client: {} });
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    void (async () => {
      try {
        const r = await httpsCallable(functions, 'noteCounts')({ orgId });
        if (live) setNoteCounts(r.data as NoteCounts);
      } catch {
        // No badges rather than no board.
      }
    })();
    return () => { live = false; };
  }, [orgId]);

  const groups = useMemo(() => groupByClient(targets), [targets]);
  const isSc = source === 'sc';
  // Machine, Status, (folders x4 | last backup), Why, expand, hide.
  const colCount = isSc ? 9 : 6;

  if (targets.length === 0) {
    return <div className="empty">{emptyMessage}</div>;
  }

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Machine / Client</th>
            <th>Status</th>
            {/* Image Manager gets the folder tally; the others get a time column.
                The old site headed it "Last Success" for ShadowProtect and
                "Last Backup" for Datto - a real distinction, since a Datto
                device reports when it last RAN and an endpoint reports when it
                last SUCCEEDED. */}
            {isSc ? (
              <>
                <th>Folders</th>
                <th className="col-num">Healthy</th>
                <th className="col-num">Warning</th>
                <th className="col-num">Critical</th>
              </>
            ) : (
              <th>{source === 'sp' ? 'Last Success' : 'Last Backup'}</th>
            )}
            <th>Why</th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {groups.map(([client, rows]) => (
            <GroupRows
              key={client}
              client={client}
              rows={rows}
              orgId={orgId}
              isSc={isSc}
              colCount={colCount}
              expanded={expanded}
              onToggle={toggle}
              canHide={canHide}
              onHide={onHide}
              noteCounts={noteCounts}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({
  client, rows, orgId, isSc, colCount, expanded, onToggle, canHide, onHide, noteCounts,
}: {
  client: string;
  rows: Target[];
  orgId?: string | null;
  isSc: boolean;
  colCount: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  canHide?: boolean;
  onHide?: (t: Target) => void;
  noteCounts: NoteCounts;
}) {
  return (
    <>
      <tr className="group-row">
        <td colSpan={colCount}>{client}</td>
      </tr>
      {rows.map((t) => {
        const agents = Array.isArray(t.detail?.agents) ? (t.detail.agents as AgentRow[]) : [];
        const folders = Array.isArray(t.detail?.folders) ? (t.detail.folders as FolderRow[]) : [];
        const counts = (t.detail?.folderCounts ?? null) as FolderCounts | null;
        const sizeMb = Number(t.detail?.folderSizeMb ?? 0);
        const tally = counts ?? (folders.length ? deriveCounts(folders) : null);
        const isOpen = expanded.has(t.id);

        /*
         * A CLIENT NOTE COUNTS FOR EVERY MACHINE THAT CLIENT OWNS.
         *
         * "They close on Fridays" is written once against the client and is the
         * answer to "why did this fail on Saturday?" on every one of their
         * machines. Counting only the machine's own notes would hide exactly
         * the note most likely to explain the row you are looking at.
         */
        const noteTotal =
          (noteCounts.target[t.id] ?? 0) + (noteCounts.client[t.client?.trim() ?? ''] ?? 0);

        // Something to open when there is sub-detail, or when the reason is
        // longer than the cell can show. Without the length test a machine with
        // a two-line reason and no folders would clip with no way to expand.
        const hasDetail = agents.length > 0 || folders.length > 0;
        // Every row expands now: even a healthy machine with a one-line reason
        // has notes worth reading and writing.
        const canExpand = true;
        void hasDetail;

        return (
          <Fragment key={t.id}>
            <tr>
              <td>
                <div className="cell-name">{t.name}</div>
                {t.missingSince && (
                  <div className="cell-flag">No longer reported by the vendor</div>
                )}
                {/*
                  The TICKET NUMBER, as the link. It is what people quote to
                  each other, so it is the text; the internal id is a handle
                  nobody recognises and is never shown.

                  Opens in a new tab on purpose - somebody checking a ticket is
                  still working through the board and should not lose it. rel
                  is there because target=_blank without it hands the opened
                  page a handle back to this one.
                */}
                {t.psaTicketNumber && (
                  <div className="cell-sub">
                    {t.psaTicketUrl ? (
                      <a href={t.psaTicketUrl} target="_blank" rel="noreferrer noopener"
                         title="Open this ticket in your PSA">
                        {t.psaTicketNumber}
                      </a>
                    ) : (
                      <span className="mono dim" title="No link available for this PSA">
                        {t.psaTicketNumber}
                      </span>
                    )}
                  </div>
                )}
              </td>
              <td><StatusPill status={t.status} reason={t.statusReason} /></td>

              {isSc ? (
                <>
                  <td className="mono col-folders">
                    {tally ? (
                      <>
                        {tally.total}
                        {sizeMb > 0 && <span className="dim"> ({formatSize(sizeMb)})</span>}
                      </>
                    ) : <span className="dim">--</span>}
                  </td>
                  {/* A zero is rendered as 0, not as a dash. The old site printed
                      "--" for every empty bucket, which is why a machine with
                      one warning and no failures looked like it had no counts at
                      all. Dimming carries "nothing here" without claiming the
                      number is missing. */}
                  <Count n={tally?.healthy} tone="ok" />
                  <Count n={tally?.warning} tone="warn" />
                  <Count n={tally?.critical} tone="fail" />
                </>
              ) : (
                <td>
                  <span className={'mono ' + staleClass(t)} title={exactWhen(t)}>
                    {lastSuccess(t)}
                  </span>
                </td>
              )}

              <td className="cell-why">
                {/* Sits with the reason because that is what somebody is
                    reading when they wonder whether anyone has looked at this
                    before. Title rather than a legend: one word explains it,
                    and a legend for a badge is a badge that failed. */}
                {noteTotal > 0 && (
                  <span
                    className="note-badge"
                    title={noteTotal === 1 ? '1 team note - open the row to read it' : `${noteTotal} team notes - open the row to read them`}
                  >
                    {noteTotal === 1 ? '1 note' : `${noteTotal} notes`}
                  </span>
                )}
                {canExpand ? (
                  <button
                    type="button"
                    className={'reason-toggle' + (isOpen ? ' is-open' : '')}
                    aria-expanded={isOpen}
                    onClick={() => onToggle(t.id)}
                    title={isOpen ? 'Collapse' : 'Show the full reason'}
                  >
                    <span className={isOpen ? 'reason-full' : 'reason-clamp'}>{t.statusReason}</span>
                    <span className="reason-more">{isOpen ? 'Less' : 'More'}</span>
                  </button>
                ) : (
                  <span className="reason-full">{t.statusReason}</span>
                )}
              </td>

              <td>
                {folders.length > 0 ? (
                  <button className="btn-link" onClick={() => onToggle(t.id)}>
                    {folders.length} folder{folders.length === 1 ? '' : 's'}
                  </button>
                ) : agents.length > 0 ? (
                  <button className="btn-link" onClick={() => onToggle(t.id)}>
                    {agents.length} agent{agents.length === 1 ? '' : 's'}
                  </button>
                ) : null}
              </td>
              <td style={{ textAlign: 'right' }}>
                {/* Hiding changes what the whole org sees, so it is an
                    owner/admin action rather than a personal preference. */}
                {canHide && onHide && (
                  <button
                    className="row-hide" title="Hide this machine from the dashboard"
                    onClick={() => onHide(t)}
                  >
                    Hide
                  </button>
                )}
              </td>
            </tr>

            {isOpen && agents.length > 0 && (
              <tr className="agent-row">
                <td colSpan={colCount}>
                  <ApplianceStrip
                        d={(t.detail ?? {}) as ApplianceDetail}
                        serial={t.sourceId ?? null}
                        orgId={orgId ?? null}
                        canDismiss={canHide === true}
                      />
                  <div className="agent-list">
                    {agents.map((a, i) => (
                      <div className="agent-item" key={a.name + i}>
                        <span className="agent-name">{a.name}</span>
                        <StatusPill status={a.status} reason={a.statusReason} />
                        <span className="mono agent-time">
                          {relative(a.lastBackup ? new Date(a.lastBackup) : null)}
                        </span>
                        {a.lastError && <span className="agent-error">{a.lastError}</span>}
                        {/* A volume nobody is backing up. Not a status - the
                            agent itself may be running perfectly - but it is
                            the thing somebody would most want to know, and it
                            has been in the payload unread all along. */}
                        <UnprotectedVolumes
                          agent={a.name}
                          serial={t.sourceId ?? null}
                          orgId={orgId ?? null}
                          unprotected={a.unprotectedVolumes ?? []}
                          confirmed={a.confirmedUnprotected ?? []}
                          canConfirm={canHide === true}
                        />
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            )}

            {/* Knowledge belongs on EVERY machine, not only the ones with
                sub-detail. A healthy machine is exactly where "the client
                turns this off at weekends" needs to be written down, before
                it goes red and somebody investigates from scratch. */}
            {isOpen && (
              <tr className="agent-row">
                <td colSpan={colCount}>
                  {/* Written at the moment somebody is looking at the machine
                      and disagreeing with its status - which is the only moment
                      anybody knows what the policy should say. */}
                  <DevicePolicyPanel
                    orgId={orgId ?? null}
                    targetId={t.id}
                    name={t.name}
                    policy={t.schedulePolicy}
                    canManage={canHide === true}
                  />
                  <KnowledgePanel target={t} orgId={orgId ?? null} />
                  {/* Only when there IS a second connection: on the common
                      single-appliance setup the word "Default" beside every
                      machine is noise, not information. */}
                  {t.connectionLabel && t.connectionLabel !== 'Default' && (
                    <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                      Found by <strong>{t.connectionLabel}</strong>
                    </div>
                  )}
                  <ActionLog orgId={orgId ?? null} targetId={t.id} />
                </td>
              </tr>
            )}

            {isOpen && folders.length > 0 && (
              <tr className="agent-row">
                <td colSpan={colCount}>
                  <FolderPanel folders={folders} counts={tally} sizeMb={sizeMb} />
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </>
  );
}

/**
 * How long ago, in the fewest characters that stay honest.
 *
 * Deliberately coarse. This is a number to SCAN down a list of twenty-seven
 * folders looking for the one that stopped, not a timestamp to quote - the
 * exact time is in the tooltip for when somebody needs it.
 */
function sinceText(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '';
  const h = ms / 3_600_000;
  if (h < 1) return 'just now';
  if (h < 48) return `${Math.round(h)}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** One numeric folder-tally cell. Zero is shown, and dimmed. */
function Count({ n, tone }: { n: number | undefined; tone: 'ok' | 'warn' | 'fail' }) {
  if (n === undefined) return <td className="col-num mono dim">--</td>;
  const cls = n === 0 ? 'dim' : tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : 'text-fail';
  return <td className={'col-num mono ' + cls}>{n}</td>;
}

/**
 * Every managed folder this machine watches.
 *
 * Each folder is a DIFFERENT protected machine's backup chain, so listing them
 * is the only way to answer "which one is broken, and is it one or fifteen".
 */
function FolderPanel({
  folders, counts, sizeMb,
}: { folders: FolderRow[]; counts: FolderCounts | null; sizeMb: number }) {
  const tally = counts ?? deriveCounts(folders);
  // Worst first: the reason someone opened this is at the top.
  const ordered = [...folders].sort(
    (a, b) => SEVERITY[a.status] - SEVERITY[b.status] || a.machine.localeCompare(b.machine)
  );

  return (
    <div className="folder-panel">
      <div className="folder-tally">
        <span><b>{tally.total}</b> folders</span>
        {sizeMb > 0 && <span><b>{formatSize(sizeMb)}</b> stored</span>}
        <span className="text-ok"><b>{tally.healthy}</b> healthy</span>
        <span className="text-warn"><b>{tally.warning}</b> warning</span>
        <span className="text-fail"><b>{tally.critical}</b> critical</span>
        {tally.unknown > 0 && <span className="dim"><b>{tally.unknown}</b> not reporting</span>}
      </div>

      <div className="folder-list">
        {ordered.map((f, i) => (
          <div className="folder-item" key={f.path + i}>
            <div className="folder-head">
              <StatusPill status={f.status} reason={f.problems.join('; ') || 'No problems reported'} />
              <span className="folder-machine">{f.machine}</span>
              <span className="folder-path mono" title={f.path}>{f.path}</span>
                {f.lastActivity && (
                  <span
                    className="folder-when mono"
                    title={`Image Manager last did something on this chain at ${new Date(f.lastActivity).toLocaleString()}`}
                  >
                    {sinceText(f.lastActivity)}
                  </span>
                )}
            </div>
            {f.problems.length > 0 ? (
              <ul className="folder-problems">
                {/* EVERY problem, not the first. A folder can be both out of
                    disk and failing verification, and hiding the second one is
                    how a fix gets called complete while the chain stays broken. */}
                {f.problems.map((p, n) => <li key={n}>{p}</li>)}
              </ul>
            ) : (
              <div className="folder-none">Nothing reported against this folder.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** MB -> TB/GB. The vendor reports megabytes; nobody reads 2490855 MB. */
function formatSize(mb: number): string {
  if (mb >= 1048576) return `${(mb / 1048576).toFixed(2)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

interface AgentRow {
  name: string;
  status: Status;
  statusReason: string;
  lastBackup: string | null;
  lastError: string | null;
  unprotectedVolumes?: string[];
  confirmedUnprotected?: string[];
}

/**
 * The appliance's own facts, as reported by Datto.
 *
 * Separate from the machine statuses on purpose. These describe the BOX - is it
 * checking in, is it full, is it still under contract - and the four of them
 * were sitting unused in the API response until v0.0.154.
 */
interface ApplianceDetail {
  lastSeenDate?: string | null;
  offlineHours?: number | null;
  storagePercent?: number | null;
  storageUsedBytes?: number | null;
  storageTotalBytes?: number | null;
  offsiteUsedBytes?: number | null;
  servicePlan?: string | null;
  servicePeriod?: string | null;
  serviceDaysLeft?: number | null;
  warrantyExpire?: string | null;
  warrantyDaysLeft?: number | null;
  vendorAlertCount?: number | null;
  remoteWebUrl?: string | null;
  serviceDismissed?: boolean;
  warrantyDismissed?: boolean;
}

function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** Days remaining, worded so a negative number does not read as nonsense. */
function renewal(days: number | null | undefined): string | null {
  if (days === null || days === undefined) return null;
  if (days < 0) return `expired ${Math.abs(days)}d ago`;
  return `${days}d left`;
}

/**
 * FACTS, NOT COLOURS.
 *
 * The thresholds already decided the row's status; this strip is for the
 * numbers behind it, and for the ones that have not crossed a threshold yet.
 * Somebody planning a renewal wants to see 90 days out, and this is now the
 * ONLY place a renewal date appears: it stopped deciding a status at all, on
 * the grounds that a contract date is not an answer to the question the status
 * column asks. A panel that only appears when something is wrong could not
 * carry it.
 *
 * Anything Datto did not report is simply absent rather than shown as zero.
 * "0 GB free" and "we were not told" must never look the same.
 */
function ApplianceStrip({
  d, serial, orgId, canDismiss,
}: {
  d: ApplianceDetail;
  serial: string | null;
  orgId: string | null;
  canDismiss: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  /*
   * OPTIMISTIC, and honest about why.
   *
   * The struck-through state is written by the COLLECTOR, so it does not change
   * until Datto is next swept - up to fifteen minutes. Waiting for that would
   * make the button look broken, and pressing it twice is exactly what somebody
   * does when nothing happens.
   *
   * So the chip flips immediately. Nothing else has to catch up, because a
   * renewal date no longer contributes to the row's status - dismissing one is
   * now only "we know about this, stop repeating it at me".
   */
  const [local, setLocal] = useState<Record<string, boolean>>({});
  const isDismissed = (kind: 'service' | 'warranty', fromServer: boolean): boolean =>
    local[kind] ?? fromServer;

  /**
   * Turn a renewal notice off, or back on.
   *
   * The DATE is sent, never a key: the server rebuilds the key with the same
   * function the classifier uses, and decides the phase itself from that date.
   * A client that could name its own phase could dismiss the "expired" notice
   * for a date that has not arrived - silencing the consequence before the
   * warning has been seen.
   */
  async function toggle(kind: 'service' | 'warranty', dateStr: string, dismissed: boolean) {
    if (!orgId || !dateStr) return;
    setBusy(kind);
    try {
      await httpsCallable(functions, 'setNoticeDismissed')({ orgId, serial, kind, dateStr, dismissed });
      setLocal((m) => ({ ...m, [kind]: dismissed }));
    } finally {
      setBusy(null);
    }
  }

  const items: {
    label: string;
    value: string;
    title?: string;
    dismissed?: boolean;
    action?: () => void;
  }[] = [];

  if (d.lastSeenDate) {
    const h = d.offlineHours ?? 0;
    items.push({
      label: 'Last seen',
      value: h < 1 ? 'just now' : h < 48 ? `${Math.round(h)}h ago` : `${Math.round(h / 24)}d ago`,
      title: new Date(d.lastSeenDate).toLocaleString(),
    });
  }
  if (d.storagePercent !== null && d.storagePercent !== undefined) {
    items.push({
      label: 'Local storage',
      value: `${Math.round(d.storagePercent)}% used`,
      title: `${bytes(d.storageUsedBytes)} of ${bytes(d.storageTotalBytes)}`,
    });
  }
  if (d.offsiteUsedBytes !== null && d.offsiteUsedBytes !== undefined) {
    items.push({ label: 'Offsite', value: bytes(d.offsiteUsedBytes) });
  }
  /*
   * The renewal facts stay on the strip whether or not they have been
   * dismissed - only the SENTENCE goes away. A suppression nobody can see is
   * one nobody revisits, the same rule that keeps hidden machines listed, and
   * somebody planning renewals still needs the date.
   */
  const svc = renewal(d.serviceDaysLeft);
  if (svc) items.push({
    label: 'Service',
    value: svc,
    title: d.servicePlan ?? undefined,
    dismissed: isDismissed('service', d.serviceDismissed === true),
    action: canDismiss && serial && d.servicePeriod
      ? () => void toggle('service', d.servicePeriod!, !isDismissed('service', d.serviceDismissed === true))
      : undefined,
  });
  const war = renewal(d.warrantyDaysLeft);
  if (war) items.push({
    label: 'Warranty',
    value: war,
    title: d.warrantyExpire ?? undefined,
    dismissed: isDismissed('warranty', d.warrantyDismissed === true),
    action: canDismiss && serial && d.warrantyExpire
      ? () => void toggle('warranty', d.warrantyExpire!, !isDismissed('warranty', d.warrantyDismissed === true))
      : undefined,
  });
  if (d.vendorAlertCount) {
    items.push({
      label: 'Datto alerts',
      value: String(d.vendorAlertCount),
      // Said out loud because a number with no severity is easy to misread as
      // one of ours. It is not: we cannot see what these alerts say.
      title: "Datto's own alert count. We cannot read their content or severity, so it does not affect the status here.",
    });
  }

  if (items.length === 0 && !d.remoteWebUrl) return null;

  return (
    <div className="appliance-strip">
      {items.map((i) => (
        <span
          className={'appliance-fact' + (i.dismissed ? ' appliance-fact-dismissed' : '')}
          key={i.label}
          title={
            i.dismissed
              ? 'Acknowledged. Still shown here, because the date has not gone anywhere.'
              : i.title
          }
        >
          <span className="appliance-label">{i.label}</span>
          <span className="appliance-value">{i.value}</span>
          {i.action && (
            <button
              type="button"
              className="appliance-dismiss"
              disabled={busy !== null}
              title={i.dismissed ? 'Show this as outstanding again' : 'Mark this renewal as handled'}
              onClick={i.action}
            >
              {i.dismissed ? 'undo' : 'dismiss'}
            </button>
          )}
        </span>
      ))}
      {d.remoteWebUrl && (
        /* Same idea as the PSA ticket number on the row: the thing you want the
           moment you have read the status is a way to go and look. */
        <a className="appliance-link" href={d.remoteWebUrl} target="_blank" rel="noreferrer">
          Open in Datto
        </a>
      )}
    </div>
  );
}

/**
 * Volumes nothing is backing up, and the ones somebody has signed off.
 *
 * WHY A CONFIRM AND NOT A HIDE. Some drives are deliberately left blank - a
 * scratch volume, a swap disk, an empty D: on a machine whose data all lives on
 * C:. Warning about those forever teaches people to read past the line, and
 * this is the line answering "is anybody backing this up at all", which is a
 * worse question to get wrong than any other on the row.
 *
 * Confirmed volumes stay listed, quietly, with a way to undo. They are not
 * removed: a suppression nobody can see is one nobody revisits, and "which
 * drives did we decide not to back up" is a question somebody eventually asks -
 * most likely during a restore.
 *
 * PER VOLUME. Confirming Z: says nothing about C:, and a volume that becomes
 * unprotected later has not been confirmed, so it still shows.
 */
function UnprotectedVolumes({
  agent, serial, orgId, unprotected, confirmed, canConfirm,
}: {
  agent: string;
  serial: string | null;
  orgId: string | null;
  unprotected: string[];
  confirmed: string[];
  canConfirm: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  /*
   * Optimistic, for the same reason as the renewal chips: the split between
   * these two lists is made by the COLLECTOR, so it does not change until Datto
   * is next swept. Moving the volume across immediately is what makes the
   * button feel like it worked; the stored answer catches up and agrees.
   */
  const [moved, setMoved] = useState<Record<string, boolean>>({});

  async function set(volume: string, confirm: boolean) {
    if (!orgId || !serial) return;
    setBusy(volume);
    try {
      await httpsCallable(functions, 'setVolumeConfirmed')({
        orgId, serial, agent, volume, confirmed: confirm,
      });
      setMoved((m) => ({ ...m, [volume]: confirm }));
    } finally {
      setBusy(null);
    }
  }

  const isConfirmed = (v: string) => moved[v] ?? confirmed.includes(v);
  const all = [...unprotected, ...confirmed];
  const open = all.filter((v) => !isConfirmed(v));
  const signed = all.filter(isConfirmed);

  if (all.length === 0) return null;

  return (
    <>
      {open.length > 0 && (
        <span className="agent-unprotected" title="No backup covers these volumes">
          not protected:{' '}
          {open.map((v, n) => (
            <span key={v}>
              {n > 0 ? ', ' : ''}
              {v}
              {canConfirm && serial && (
                <button
                  type="button"
                  className="appliance-dismiss"
                  disabled={busy !== null}
                  title={'Confirm that ' + v + ' is meant to have no backup'}
                  onClick={() => void set(v, true)}
                >
                  confirm
                </button>
              )}
            </span>
          ))}
        </span>
      )}
      {signed.length > 0 && (
        <span
          className="agent-unprotected agent-unprotected-ok"
          title="Confirmed as intentionally not backed up"
        >
          intentionally blank:{' '}
          {signed.map((v, n) => (
            <span key={v}>
              {n > 0 ? ', ' : ''}
              {v}
              {canConfirm && serial && (
                <button
                  type="button"
                  className="appliance-dismiss"
                  disabled={busy !== null}
                  title={'Warn about ' + v + ' again'}
                  onClick={() => void set(v, false)}
                >
                  undo
                </button>
              )}
            </span>
          ))}
        </span>
      )}
    </>
  );
}

/** Mirrors FolderDetail in the ShadowControl collector. */
interface FolderRow {
  path: string;
  machine: string;
  state: number;
  /** When this chain last did anything. Null when it has never reported one. */
  lastActivity?: string | null;
  status: Status;
  problems: string[];
}

interface FolderCounts {
  total: number;
  healthy: number;
  warning: number;
  critical: number;
  unknown: number;
}

/** Fallback for targets written before the collector started sending counts. */
function deriveCounts(folders: FolderRow[]): FolderCounts {
  return {
    total: folders.length,
    healthy: folders.filter((f) => f.status === 'healthy').length,
    warning: folders.filter((f) => f.status === 'warning').length,
    critical: folders.filter((f) => f.status === 'failed').length,
    unknown: folders.filter((f) => f.status === 'unknown').length,
  };
}

const SEVERITY: Record<Status, number> = {
  failed: 0, warning: 1, unknown: 2, paused: 3, healthy: 4,
};

function groupByClient(targets: Target[]): Array<[string, Target[]]> {
  const map = new Map<string, Target[]>();
  for (const t of targets) {
    // A blank client is a real state, not a reason to disappear.
    const key = t.client?.trim() || 'No client assigned';
    const list = map.get(key);
    if (list) list.push(t); else map.set(key, [t]);
  }
  for (const list of map.values()) {
    list.sort((a, b) => SEVERITY[a.status] - SEVERITY[b.status] || a.name.localeCompare(b.name));
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

/**
 * How long since this machine last succeeded.
 *
 * NEVER render `lastSuccessText` directly. For ShadowProtect the collector
 * sets it to `lastBackup.toISOString()`, so the column was printing a raw
 * timestamp - "2026-08-07T04:30:14.668Z" where the old site said "184 days".
 *
 * `lastSuccessHours` is the honest field: a number the collector computed from
 * the vendor's absolute timestamp. Formatting it here means one wording for
 * every source, and it matches the vendor's own phrasing ("13 hours",
 * "184 days") rather than inventing a second vocabulary.
 */
function lastSuccess(t: Target): string {
  const h = t.lastSuccessHours;
  if (h === null || h === undefined) {
    // Datto reports a snapshot time rather than an age.
    const d = t.lastBackup?.toDate?.() ?? null;
    return d ? relative(d) : '--';
  }
  if (h < 1 / 60) return 'just now';
  if (h < 1) return `${Math.round(h * 60)} min.`;
  if (h < 48) return `${Math.round(h)} hours`;
  const days = Math.round(h / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** The precise moment, on hover. "184 days" is the answer; this is the proof. */
function exactWhen(t: Target): string {
  const d = t.lastBackup?.toDate?.() ?? null;
  if (d) return d.toLocaleString();
  return t.lastSuccessHours === null || t.lastSuccessHours === undefined
    ? 'No successful backup has been reported'
    : '';
}

function staleClass(t: Target): string {
  const h = t.lastSuccessHours;
  if (h === null || h === undefined) return 'dim';
  if (h > 48) return 'text-fail';
  if (h > 26) return 'text-warn';
  return '';
}

function relative(d: Date | null): string {
  if (!d) return '--';
  const mins = (Date.now() - d.getTime()) / 60000;
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}
